import { parseSignInIdentifier } from '@chukua/common';
import { Alert, Button, Form, Input } from 'antd';
import { AtSign, KeyRound } from 'lucide-react';
import type { SignInCode } from '../hooks/use-sign-in-code';
import { INVALID_IDENTIFIER_MESSAGE } from '../policies/sign-in-code-copy.policy';

interface StartFormValues {
  identifier?: string;
}

interface LoginStartFormProps {
  title: string;
  step: SignInCode;
  onUsePassword: () => void;
}

const identifierRule = {
  validator: (_: unknown, value: string | undefined) =>
    parseSignInIdentifier(value ?? '') ? Promise.resolve() : Promise.reject(new Error(INVALID_IDENTIFIER_MESSAGE)),
};

/** The first step: one field for an email or a mobile, and the code goes to it. The password is the other way in. */
export function LoginStartForm({ title, step, onUsePassword }: LoginStartFormProps) {
  return (
    <>
      <h1 className="text-2xl font-semibold tracking-tight text-fg">{title}</h1>
      <p className="mt-1.5 mb-8 text-sm text-muted">Enter your email or mobile number and we&apos;ll send you a 6-digit code. No password needed.</p>
      {step.error && <Alert className="mb-5" type="error" title={step.error} showIcon />}
      <Form<StartFormValues>
        layout="vertical"
        size="large"
        requiredMark={false}
        initialValues={{ identifier: step.typed }}
        onValuesChange={(_, values) => step.setTyped(values.identifier ?? '')}
        onFinish={(values) => void step.sendFirstCode(values.identifier ?? '')}
        disabled={step.submitting}
      >
        <Form.Item name="identifier" label="Email or mobile number" rules={[identifierRule]} extra={step.hint}>
          <Input prefix={<AtSign className="size-4 text-muted" />} autoComplete="username" autoFocus placeholder="you@company.co.ke or 0712 345 678" />
        </Form.Item>
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" block loading={step.submitting}>
            Send me a code
          </Button>
        </Form.Item>
      </Form>
      <div className="my-6 flex items-center gap-3 text-sm text-muted" role="separator">
        <span className="h-px flex-1 bg-line" />
        or
        <span className="h-px flex-1 bg-line" />
      </div>
      <Button size="large" block icon={<KeyRound className="size-4" />} onClick={onUsePassword}>
        Sign in with a password
      </Button>
    </>
  );
}
